/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react'

import {
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiForm,
  EuiSpacer,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { RulesetType } from '@modules/network-security-lib/types'
import type { DeploymentGetResponse } from '@modules/cloud-api/v1/types'
import { CuiAlert } from '@modules/cui/Alert'
import type { AnyUserProject } from '@modules/project-user-api/types'

import IpFilterForm from './IpFilterForm'
import PrivateConnectionForm from './PrivateConnectionForm'
import HostedTrafficFilterAssociationHandler from './HostedTrafficFilterAssociationHandler'
import ServerlessTrafficFilterAssociationHandler from './ServerlessTrafficFilterAssociationHandler'
import { ApplyAssociationButton } from './ApplyAssociationButton'

import type { TrafficFilterAssociationHandlerRefProps } from './types'

type Props = {
  rulesetType: RulesetType
  onClose: () => void
} & (
  | {
      deployment: DeploymentGetResponse
      project?: never
    }
  | {
      project: AnyUserProject
      deployment?: never
    }
)

const TrafficFilterAssociationForm: React.FunctionComponent<Props> = ({
  rulesetType,
  onClose,
  deployment,
  project,
}) => {
  const handlerRef = useRef<TrafficFilterAssociationHandlerRefProps | null>(null)

  const save = () => {
    handlerRef.current?.save()
  }

  const [isSubmittable, setIsSubmittable] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<unknown>()

  const onAssociationChange = useCallback((args: { isSubmittable: boolean }) => {
    setIsSubmittable(args.isSubmittable)
  }, [])

  const onFormStateChange = useCallback((args: { isSaving: boolean; error: unknown }) => {
    setIsSaving(args.isSaving)
    setError(args.error)
  }, [])

  const form = useMemo(() => {
    const InnerForm = rulesetType === 'ipFilter' ? IpFilterForm : PrivateConnectionForm

    if (deployment) {
      return (
        <HostedTrafficFilterAssociationHandler
          ref={handlerRef}
          onClose={onClose}
          onAssociationChange={onAssociationChange}
          onFormStateChange={onFormStateChange}
          deployment={deployment}
        >
          {(props) => <InnerForm {...props} />}
        </HostedTrafficFilterAssociationHandler>
      )
    }

    if (project) {
      return (
        <ServerlessTrafficFilterAssociationHandler
          ref={handlerRef}
          onClose={onClose}
          onAssociationChange={onAssociationChange}
          onFormStateChange={onFormStateChange}
          project={project}
        >
          {(props) => <InnerForm {...props} />}
        </ServerlessTrafficFilterAssociationHandler>
      )
    }

    return null
  }, [rulesetType, deployment, project])

  return (
    <EuiFlyout onClose={onClose} size='40rem' aria-labelledby='trafficFilterAssociationFormTitle'>
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2 id='trafficFilterAssociationFormTitle'>
            {rulesetType === 'ipFilter' ? (
              <FormattedMessage
                id='trafficFilterAssociationForm.title.ipFilter'
                defaultMessage='Apply IP filter'
              />
            ) : (
              <FormattedMessage
                id='trafficFilterAssociationForm.title.privateConnection'
                defaultMessage='Apply private connection'
              />
            )}
          </h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <EuiForm>
          {form}
          <EuiSpacer size='m' />
          {error && <CuiAlert type='danger'>{error}</CuiAlert>}
        </EuiForm>
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty onClick={onClose} data-test-id='cancel-button'>
              <FormattedMessage id='trafficFilterAssociationForm.cancel' defaultMessage='Cancel' />
            </EuiButtonEmpty>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <ApplyAssociationButton
              isLoading={isSaving}
              isSubmittable={isSubmittable}
              onClick={save}
              deployment={deployment}
              project={project}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default TrafficFilterAssociationForm
